import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';

export default function AboutPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="public-page">
      <SEO
        title="About Us - The Story Behind Hangrily"
        description="Hangrily was born from a simple frustration: couples and friends wasting time arguing about where to eat. Learn about our mission to end restaurant indecision forever."
        path="/about"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
            { '@type': 'ListItem', position: 2, name: 'About', item: 'https://hangrily.app/about' }
          ]
        }}
      />
      <PublicHeader />

      <main className="page-content">
        <section className="page-hero">
          <h1>About Hangrily</h1>
          <p className="hero-subtitle">
            We're on a mission to end restaurant indecision, one swipe at a time.
          </p>
        </section>

        <section className="content-section">
          <div className="about-story">
            <h2 className="section-title">Our Story</h2>
            <p>
              Hangrily was born from a simple, universal frustration: the endless "where should we eat?" debate. 
              We've all been there—hungry, indecisive, scrolling through restaurant apps for 30 minutes, only to 
              end up at the same place we always go.
            </p>
            <p>
              In 2026, we decided enough was enough. We built Hangrily to solve this problem once and for all. 
              By combining the addictive simplicity of Tinder-style swiping with smart matching algorithms, we 
              created a way for individuals, couples, and groups to make restaurant decisions in minutes instead of hours.
            </p>
            <p>
              What started as a weekend project to help couples stop arguing about dinner has grown into a platform 
              used by thousands of people every day. We're proud to have saved countless relationships, friendships, 
              and family dinners from the dreaded decision paralysis.
            </p>
          </div>

          <div className="mission-section">
            <h2 className="section-title">Our Mission</h2>
            <div className="mission-grid">
              <div className="mission-card">
                <div className="mission-icon">🎯</div>
                <h3>Simplify Decisions</h3>
                <p>
                  Make restaurant decisions fast, fair, and fun. No more endless debates or decision fatigue.
                </p>
              </div>
              <div className="mission-card">
                <div className="mission-icon">❤️</div>
                <h3>Strengthen Relationships</h3>
                <p>
                  Help couples and groups find common ground and spend less time arguing, more time enjoying meals together.
                </p>
              </div>
              <div className="mission-card">
                <div className="mission-icon">🌟</div>
                <h3>Discover New Places</h3>
                <p>
                  Introduce users to restaurants they might never have tried, expanding their culinary horizons.
                </p>
              </div>
              <div className="mission-card">
                <div className="mission-icon">🆓</div>
                <h3>Accessible & Ad-Free</h3>
                <p>
                  Keep Hangrily accessible with a generous free tier and zero ads or sponsored listings. Premium features at an affordable price.
                </p>
              </div>
            </div>
          </div>

          <div className="values-section">
            <h2 className="section-title">Our Values</h2>
            <div className="values-list">
              <div className="value-item">
                <h3>🔒 Privacy First</h3>
                <p>
                  Your data is yours. We never sell your information, and we're transparent about what we collect and why.
                </p>
              </div>
              <div className="value-item">
                <h3>⚡ Speed & Simplicity</h3>
                <p>
                  We believe the best apps are the ones that get out of your way. Hangrily is designed to be fast, 
                  intuitive, and distraction-free.
                </p>
              </div>
              <div className="value-item">
                <h3>🤝 User-Centric</h3>
                <p>
                  Every feature we build is based on real user feedback. We listen, iterate, and improve based on 
                  what you tell us.
                </p>
              </div>
              <div className="value-item">
                <h3>🌍 Inclusive</h3>
                <p>
                  Hangrily works for everyone—solo diners, couples, friend groups, families, and anyone who loves food.
                </p>
              </div>
              <div className="value-item">
                <h3>💡 Innovation</h3>
                <p>
                  We're constantly exploring new ways to make restaurant decisions better, from AI recommendations 
                  to smart filters.
                </p>
              </div>
            </div>
          </div>

          <div className="stats-section">
            <h2 className="section-title">Hangrily by the Numbers</h2>
            <div className="about-stats-row">
              <div className="about-stat">
                <span className="about-stat-number">10K+</span>
                <span className="about-stat-label">Happy Users</span>
              </div>
              <div className="about-stat-divider" />
              <div className="about-stat">
                <span className="about-stat-number">50K+</span>
                <span className="about-stat-label">Decisions Made</span>
              </div>
              <div className="about-stat-divider" />
              <div className="about-stat">
                <span className="about-stat-number">&lt;2 min</span>
                <span className="about-stat-label">Avg. Decision</span>
              </div>
              <div className="about-stat-divider" />
              <div className="about-stat">
                <span className="about-stat-number">95%</span>
                <span className="about-stat-label">Satisfaction</span>
              </div>
            </div>
          </div>

          <div className="team-section">
            <h2 className="section-title">Built with Love</h2>
            <p className="team-description">
              Hangrily is built by a small, passionate team of food lovers and problem solvers. We're developers, 
              designers, and restaurant enthusiasts who believe technology should make life easier, not more complicated.
            </p>
            <p className="team-description">
              We're based in the United States but serve users worldwide. Our team is remote-first, which means we 
              understand the challenges of coordinating group decisions across time zones and preferences.
            </p>
          </div>

          <div className="future-section">
            <h2 className="section-title">What's Next</h2>
            <p>
              We're constantly working to make Hangrily better. Here's what's on our roadmap:
            </p>
            <ul className="feature-list">
              <li>🤖 AI-powered restaurant recommendations based on your taste profile</li>
              <li>🍽️ Dietary restriction filters (vegetarian, vegan, gluten-free, etc.)</li>
              <li>📱 Enhanced mobile app features</li>
              <li>🌍 International expansion with support for more cities and countries</li>
              <li>🎉 Group event planning and reservation integration</li>
              <li>⭐ Restaurant collections and favorites</li>
              <li>📊 Advanced analytics and insights</li>
            </ul>
          </div>
        </section>

        <section className="cta-banner">
          <h2>Join the Hangrily Community</h2>
          <p>Be part of the movement to end restaurant indecision forever.</p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
            Try It Free — No Account Needed
          </button>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
